import {Carousel, ProgressBar} from '@jobseeker/lotus/components';
import {Box, Card, CardContent, IconButton, Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {ClientType, JDQueryParams} from '../type';
import {TrackEventUBA} from '@jobseeker/common-core/utils';
import {
  useInView,
  useIsCustomWidth,
  useIsLargeDevice,
} from '@jobseeker/lotus/hooks';
import {Image} from '@jobseeker/lotus/components/Image';
import {slideLeft, slideRight} from '@jobseeker/lotus/assets/icons';
import {Tooltip} from '@jobseeker/lotus/elements';

type ClientSectionProps = {
  Clients?: ClientType[];
  templateInfo?: {
    templateNo?: string;
    origin?: string;
  };
  jobInfo?: any;
  jdQueryParams?: JDQueryParams;
};
const Clients: React.FC<ClientSectionProps> = ({
  Clients,
  templateInfo,
  jobInfo,
  jdQueryParams,
}) => {
  const {ref, isInView} = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });
  const isAboveLg = useIsLargeDevice(1100);

  const [companyAwardsTracked, setCompanyAwardsTracked] = useState(false);
  const carouselElements =
    Clients?.map((item, index) => {
      return (
        <Card
          key={index}
          variant="outlined"
          sx={{
            border: '1px solid rgba(126, 126, 126, 0.20)',
            width: 110,
            height: 96,
            borderRadius: 3,
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              p: 1.5,
            }}
          >
            <Box
              borderRadius={2}
              border="1px solid var(--Light-Neutral-N300, #F7F7F9)"
              bgcolor={'#FFF'}
              height={36}
              width={36}
            >
              <Image
                src={item.logoUrl}
                alt={item?.name || ''}
                width={36}
                height={36}
                style={{borderRadius: '8px'}}
              />
            </Box>
            <Tooltip title={item.name || ''} arrow>
              <Typography
                fontSize={12}
                fontWeight={500}
                lineHeight="14px"
                textAlign="center"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-word',
                  cursor: 'pointer',
                }}
              >
                {item.name}
              </Typography>
            </Tooltip>
          </CardContent>
        </Card>
      );
    }) || [];

  useEffect(() => {
    if (isInView && !companyAwardsTracked) {
      TrackEventUBA('sectionView', 'view', {
        ref: jdQueryParams?.ref || '',
        pageName: 'JD',
        jobID: jobInfo?.id,
        page: 'JD',
        templateID: templateInfo?.templateNo,
        isBranded: 'Yes',
        companyID: jobInfo?.companyData?.companyId,
        centralCompanyID: jobInfo?.recruiter?.centralizedCompanyId,
        companyName: jobInfo?.companyData?.companyName,
        section: 'Clients',
        sessionUsername: sessionStorage.getItem('userId') || '',
      });
      setCompanyAwardsTracked(true);
    }
  }, [isInView, companyAwardsTracked]);
  const isSmall = useIsCustomWidth(700);
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <Box p={2} pl={1.5} pt={1.5} pb={2.5} ref={ref}>
      <Card
        variant="outlined"
        sx={{
          border: 'none',
          backgroundColor: '#F9FAFB',
          position: 'relative',
        }}
      >
        {!isSmall && (
          <IconButton
            disableRipple
            disableFocusRipple
            className="previous-featured-client carousel-nav-button"
            onClick={() => {
              setActiveIndex(activeIndex - 1);
            }}
            sx={{
              position: 'absolute',
              left: -8,
              display: isAboveLg
                ? carouselElements?.length > 2 && activeIndex > 0
                  ? 'block'
                  : 'none'
                : carouselElements?.length > 1 && activeIndex > 0
                  ? 'block'
                  : 'none',
              transform: 'translateY(42%)',
              zIndex: 2,
              objectFit: 'cover',

              ':hover': {backgroundColor: 'transparent'},
            }}
          >
            <Image
              src={slideLeft}
              width={34}
              height={34}
              alt="left"
              objectFit="contain"
              style={{
                filter: 'drop-shadow(0px 2.4px 2.4px rgba(0, 0, 0, 0.25))',
              }}
            />
          </IconButton>
        )}
        <Carousel
          swiperProps={{slidesOffsetAfter: 10}}
          brandedJd={true}
          carouselItems={carouselElements}
          fullFrame={false}
          slidesPerView={2.5}
          next=".next-featured-client"
          previous=".previous-featured-client"
          breakpointOverrides={{
            360: {
              slidesPerView: 2.5,
              spaceBetween: 14,
            },
            380: {
              slidesPerView: 2.6,
              spaceBetween: 14,
            },
            400: {
              slidesPerView: 2.7,
              spaceBetween: 14,
            },
            420: {
              slidesPerView: 2.9,
              spaceBetween: 14,
            },
            480: {
              slidesPerView: 3,
              spaceBetween: 14,
            },

            768: {slidesPerView: 2.5, spaceBetween: 14},
            1024: {
              slidesPerView: 1.9,
              spaceBetween: 14,
            },
            1100: {
              slidesPerView: 2.2,
              spaceBetween: 14,
            },

            1280: {
              slidesPerView: 2.2,
              spaceBetween: 14,
            },
            1336: {
              slidesPerView: 2.6,
              spaceBetween: 14,
            },

            1440: {
              slidesPerView: 2.7,
              spaceBetween: 14,
            },
            1550: {
              slidesPerView: 2.7,
              spaceBetween: 18,
            },
          }}
        />
        {!isSmall && (
          <IconButton
            disableRipple
            disableFocusRipple
            className="next-featured-client carousel-nav-button"
            onClick={() => {
              setActiveIndex(activeIndex + 1);
            }}
            sx={{
              display: isAboveLg
                ? carouselElements?.length > 2
                  ? 'block'
                  : 'none'
                : carouselElements?.length > 1
                  ? 'block'
                  : 'none',
              right: -8,
              position: 'absolute',
              transform: 'translateY(-120%)',
              zIndex: 2,
              ':hover': {backgroundColor: 'transparent'},
            }}
          >
            <Image
              src={slideRight}
              width={34}
              height={34}
              alt="right"
              style={{
                filter: 'drop-shadow(0px 2.4px 2.4px rgba(0, 0, 0, 0.25))',
              }}
            />
          </IconButton>
        )}
      </Card>
    </Box>
  );
};

export default Clients;
